(() => {
  if (window.__predictiveSearchAccessibilityInitialized) return;
  window.__predictiveSearchAccessibilityInitialized = true;

  const PRODUCT_LIMIT = 8;
  const DEBOUNCE_MS = 220;
  const MIN_CHARS = 2;
  const RECENT_KEY = 'otto:recent-searches';
  const RECENT_MAX = 5;

  const readRecentSearches = () => {
    try {
      const raw = window.localStorage.getItem(RECENT_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed.filter((term) => typeof term === 'string' && term.trim()) : [];
    } catch (error) {
      return [];
    }
  };

  const writeRecentSearches = (terms) => {
    try {
      window.localStorage.setItem(RECENT_KEY, JSON.stringify(terms.slice(0, RECENT_MAX)));
      return true;
    } catch (error) {
      return false;
    }
  };

  const saveRecentSearch = (term) => {
    const value = (term || '').trim();
    if (value.length < MIN_CHARS) return;
    const next = [value, ...readRecentSearches().filter((item) => item.toLowerCase() !== value.toLowerCase())];
    writeRecentSearches(next);
  };

  const renderRecentSearches = (container) => {
    if (!container || container.dataset.renderingRecent === 'true') return;
    container.dataset.renderingRecent = 'true';

    try {
      container.querySelectorAll('[data-recent-searches]').forEach((section) => {
        const list = section.querySelector('[data-recent-searches-list]');
        if (!list) return;

        const terms = readRecentSearches();
        if (list.dataset.renderedTerms === terms.join('|')) {
          section.classList.toggle('hidden', terms.length === 0);
          return;
        }

        list.dataset.renderedTerms = terms.join('|');
        list.replaceChildren();

        if (!terms.length) {
          section.classList.add('hidden');
          return;
        }

        const fragment = document.createDocumentFragment();
        terms.forEach((term) => {
          const item = document.createElement('li');
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'search-empty-state__chip';
          button.setAttribute('data-search-chip', '');
          button.setAttribute('data-search-term', term);
          button.textContent = term;
          item.appendChild(button);
          fragment.appendChild(item);
        });
        list.appendChild(fragment);
        section.classList.remove('hidden');
      });
    } finally {
      delete container.dataset.renderingRecent;
    }
  };

  const bindPredictiveRequests = (container) => {
    const input = container.querySelector('[data-element="search-input"]');
    const dialog = container.querySelector('#search-modal');
    const results = container.querySelector('[data-element="predictive-search"]');
    const defaults = container.querySelector('[data-element="default-results"]');
    if (!input || !dialog || !results || !defaults) return;
    let revision = 0;
    let debounceTimer;
    let controller;
    const clear = () => {
      revision++;
      clearTimeout(debounceTimer);
      controller?.abort();
      results.removeAttribute('aria-busy');
    };
    const showDefaults = () => {
      results.classList.add('hidden');
      results.innerHTML = '';
      defaults.classList.remove('hidden');
    };
    input.addEventListener('input', event => {
      event.stopImmediatePropagation();
      clear();
      const query = input.value.trim();
      const current = revision;
      if (query.length < MIN_CHARS) { showDefaults(); return; }
      const skeleton = container.querySelector('[data-search-skeleton]');
      results.innerHTML = skeleton?.innerHTML || '';
      results.classList.remove('hidden');
      results.setAttribute('aria-busy', 'true');
      defaults.classList.add('hidden');
      debounceTimer = setTimeout(async () => {
        const active = controller = new AbortController();
        let timeout;
        const isCurrent = () => current === revision && input.value.trim() === query && dialog.open;
        try {
          const url = new URL(container.dataset.searchUrl, window.location.origin);
          url.searchParams.set('q', query);
          url.searchParams.set('section_id', 'predictive-search');
          url.searchParams.set('resources[type]', 'product,collection,page');
          url.searchParams.set('resources[limit]', String(PRODUCT_LIMIT));
          url.searchParams.set('resources[options][unavailable_products]', 'last');
          url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,vendor,variants.sku,variants.barcode');
          const html = await Promise.race([
            fetch(url.toString(), { signal: active.signal }).then(response => {
              if (!response.ok) throw new Error('Search unavailable');
              return response.text();
            }),
            new Promise((_, reject) => {
              timeout = setTimeout(() => { active.abort(); reject(new Error('Search timeout')); }, 8000);
            }),
          ]);
          if (!isCurrent()) return;
          const section = new DOMParser().parseFromString(html, 'text/html').querySelector('#shopify-section-predictive-search');
          if (!section) throw new Error('Missing search results');
          results.innerHTML = section.innerHTML;
        } catch (error) {
          if (!isCurrent()) return;
          window.__ottoReportUIError?.('search');
          showDefaults();
          const link = document.createElement('a');
          const form = input.closest('form');
          const url = new URL(form.action, window.location.origin);
          url.search = new URLSearchParams(new FormData(form)).toString();
          link.href = url.toString();
          link.className = 'block p-4 underline';
          link.textContent = dialog.dataset.searchRetryLabel;
          results.append(link);
          results.classList.remove('hidden');
        } finally {
          clearTimeout(timeout);
          if (current === revision) results.removeAttribute('aria-busy');
        }
      }, DEBOUNCE_MS);
    }, true);
    dialog.addEventListener('close', () => { clear(); showDefaults(); });
  };

  class PredictiveSearchListbox {
    constructor(container) {
      this.container = container;
      this.input = container.querySelector('[data-element="search-input"]');
      this.form = this.input?.closest('form');
      this.resultsHost = container.querySelector('[data-element="predictive-search"]');
      this.dialog = container.querySelector('#search-modal');
      this.activeIndex = -1;
      this.options = [];
      this.listbox = null;

      if (!this.input || !this.resultsHost) return;

      this.onKeyDown = this.onKeyDown.bind(this);
      this.onResultsUpdated = this.onResultsUpdated.bind(this);
      this.onDialogClick = this.onDialogClick.bind(this);

      this.input.addEventListener('keydown', this.onKeyDown);
      this.form?.addEventListener('submit', () => saveRecentSearch(this.input.value));
      this.dialog?.addEventListener('click', this.onDialogClick);

      this.observer = new MutationObserver(() => {
        this.onResultsUpdated();
      });
      this.observer.observe(this.resultsHost, { childList: true });

      renderRecentSearches(this.dialog || this.container);
    }

    onDialogClick(event) {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const chip = target.closest('[data-search-chip]');
      if (chip) {
        const term = chip.getAttribute('data-search-term') || chip.textContent || '';
        this.runSearch(term);
        return;
      }

      if (target.closest('[data-clear-recent-searches]')) {
        writeRecentSearches([]);
        renderRecentSearches(this.dialog || this.container);
      }
    }

    runSearch(term) {
      const value = (term || '').trim();
      if (!value) return;
      const budgetLink = this.container.querySelector('[data-budget-search-url]');
      if (budgetLink && /^rødvin under 150(?: kr)?$/i.test(value)) {
        window.location.assign(budgetLink.href);
        return;
      }

      this.input.value = value;
      saveRecentSearch(value);
      this.input.dispatchEvent(new Event('input', { bubbles: true }));
      this.input.focus();
    }

    onResultsUpdated() {
      this.listbox = this.resultsHost.querySelector('[role="listbox"]');
      this.options = this.listbox
        ? Array.from(this.listbox.querySelectorAll('[data-predictive-search-option]'))
        : [];

      this.activeIndex = -1;
      this.syncExpanded();
      this.syncActiveOption();
    }

    syncExpanded() {
      const isOpen = Boolean(this.dialog && (this.dialog.open || this.dialog.hasAttribute('open')));
      const hasQuery = this.input.value.trim().length >= MIN_CHARS;
      const hasResults = this.options.length > 0;
      const expanded = isOpen && hasQuery && hasResults;

      this.input.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      this.input.setAttribute('aria-controls', 'predictive-search-listbox');
    }

    syncActiveOption() {
      this.options.forEach((option, index) => {
        const isActive = index === this.activeIndex;
        option.setAttribute('aria-selected', isActive ? 'true' : 'false');
        option.classList.toggle('search-product-hit--active', isActive);
      });

      if (this.activeIndex >= 0 && this.options[this.activeIndex]) {
        this.input.setAttribute('aria-activedescendant', this.options[this.activeIndex].id);
        this.options[this.activeIndex].scrollIntoView({ block: 'nearest' });
      } else {
        this.input.removeAttribute('aria-activedescendant');
      }
    }

    moveActive(delta) {
      if (!this.options.length) return;

      if (this.activeIndex === -1) {
        this.activeIndex = delta > 0 ? 0 : this.options.length - 1;
      } else {
        this.activeIndex = (this.activeIndex + delta + this.options.length) % this.options.length;
      }

      this.syncActiveOption();
    }

    activateCurrentOption() {
      const option = this.options[this.activeIndex];
      if (!option) return;
      option.click();
    }

    onKeyDown(event) {
      switch (event.key) {
        case 'ArrowDown':
          if (!this.options.length) return;
          event.preventDefault();
          this.moveActive(1);
          break;
        case 'ArrowUp':
          if (!this.options.length) return;
          event.preventDefault();
          this.moveActive(-1);
          break;
        case 'Home':
          if (!this.options.length) return;
          event.preventDefault();
          this.activeIndex = 0;
          this.syncActiveOption();
          break;
        case 'End':
          if (!this.options.length) return;
          event.preventDefault();
          this.activeIndex = this.options.length - 1;
          this.syncActiveOption();
          break;
        case 'Enter':
          if (this.activeIndex >= 0) {
            event.preventDefault();
            this.activateCurrentOption();
          } else {
            saveRecentSearch(this.input.value);
          }
          break;
        case 'Escape':
          event.preventDefault();
          event.stopPropagation();
          if (this.activeIndex >= 0) {
            this.activeIndex = -1;
            this.syncActiveOption();
            this.input.focus();
            return;
          }
          this.input.focus();
          this.dialog?.querySelector('[data-element="close-button"]')?.click();
          break;
        default:
          break;
      }
    }
  }

  const init = () => {
    document.querySelectorAll('[data-module="search"]').forEach((container) => {
      if (container.dataset.predictiveListboxBound === 'true') return;
      container.dataset.predictiveListboxBound = 'true';
      bindPredictiveRequests(container);
      new PredictiveSearchListbox(container);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
